import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

const InfiniteScroll = ({
  children,
  hasMore,
  loading,
  onLoadMore,
  threshold = 50, // Reduced from 100px to 50px
  loadingComponent = null,
}) => {
  const observerRef = useRef();
  const loadingRef = useRef(null);

  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          // Check if user has scrolled to the bottom
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const isNearBottom = scrollTop + windowHeight >= documentHeight - 200;

          // Only trigger when actually intersecting and not loading and scrolled to bottom
          if (entry.isIntersecting && hasMore && !loading && isNearBottom) {
            onLoadMore();
          }
        },
        {
          rootMargin: `${threshold}px`,
          threshold: 0.1, // Only trigger when 10% element visible
        }
      );

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [loading, hasMore, onLoadMore, threshold]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const defaultLoadingComponent = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      py={2}
      ref={loadingRef}
    >
      <CircularProgress size={24} />
      <Typography variant="body2" sx={{ ml: 1 }}>
        読み込み中...
      </Typography>
    </Box>
  );

  return (
    <Box>
      {children}
      {hasMore && (
        <div ref={lastElementRef}>
          {loadingComponent || defaultLoadingComponent}
        </div>
      )}
    </Box>
  );
};

export default InfiniteScroll;
